import { useEffect, useState } from 'react';
import { Search, Ship, Plus, Flag, Briefcase, Pencil, Archive } from 'lucide-react';
import { api } from '@/lib/api';
import NewCrewForm from '@/components/NewCrewForm';
import CrewDetail from '@/components/CrewDetail';
import CaseDetail from '@/components/CaseDetail';

export default function Crew({ user }) {
  const [crew, setCrew] = useState([]);
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openCrewId, setOpenCrewId] = useState(null);
  const [openCaseId, setOpenCaseId] = useState(null);
  const load = () => api(`/crew?q=${encodeURIComponent(q)}&include_archived=${showArchived}`).then(setCrew);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, showArchived]);

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">ER SYSTEM</span>
          <h1>Crew records</h1>
          <p className="muted">A clear view of the people behind every case.</p>
        </div>
        <button data-testid="new-crew-button" className="primary-btn" onClick={() => setShowNew(true)}>
          <Plus size={17} /> Add crew
        </button>
      </div>
      <div className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input data-testid="crew-search-input" placeholder="Search by name, crew ID, position or vessel" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button
          type="button"
          className={`chip ${showArchived ? 'active' : ''}`}
          data-testid="toggle-archived-crew"
          onClick={() => setShowArchived((v) => !v)}
        >
          <Archive size={13} /> {showArchived ? 'Showing archived' : 'Show archived'}
        </button>
      </div>
      <section className="panel crew-grid">
        {crew.length === 0 && <div className="empty-inline">No crew members found.</div>}
        {crew.map((c) => {
          const displayName = (c.first_name && c.last_name) ? `${c.first_name} ${c.last_name}` : c.name;
          const initials = c.initials || (c.first_name?.[0] || '') + (c.last_name?.[0] || '') || displayName.split(' ').map((x) => x[0]).join('');
          const archived = c.status === 'Archived';
          return (
            <div
              data-testid={`crew-card-${c.id}`}
              className={`crew-card ${archived ? 'archived' : ''}`}
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => setOpenCrewId(c.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') setOpenCrewId(c.id); }}
            >
              <div className="crew-avatar">{initials.slice(0, 2).toUpperCase()}</div>
              <div className="crew-info">
                <strong>{displayName}</strong>
                {c.crew_id && <span className="crew-id-badge">{c.crew_id}</span>}
                <span>{c.position || c.rank}{c.department ? ` · ${c.department}` : ''}</span>
                <small><Ship size={12} /> {c.vessel}</small>
                {c.nationality && <small><Flag size={12} /> {c.nationality}</small>}
                {(c.hire_date || c.completed_contracts > 0) && (
                  <small><Briefcase size={12} /> {c.hire_date ? `Since ${c.hire_date}` : ''}{c.hire_date && c.completed_contracts > 0 ? ' · ' : ''}{c.completed_contracts > 0 ? `${c.completed_contracts} contract${c.completed_contracts === 1 ? '' : 's'}` : ''}</small>
                )}
              </div>
              <div className="crew-actions">
                <span className={archived ? 'archived-tag' : 'active-dot'}>{archived ? 'Archived' : 'Active'}</span>
                <button
                  type="button"
                  className="ghost-btn tiny"
                  data-testid={`edit-crew-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                >
                  <Pencil size={13} /> Edit
                </button>
              </div>
            </div>
          );
        })}
      </section>
      {showNew && <NewCrewForm user={user} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {editing && (
        <NewCrewForm
          user={user}
          crew={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
        />
      )}
      {openCrewId && !editing && (
        <CrewDetail
          crewId={openCrewId}
          onClose={() => setOpenCrewId(null)}
          onEdit={(c) => setEditing(c)}
          onChanged={load}
          onOpenCase={(id) => setOpenCaseId(id)}
        />
      )}
      {openCaseId && <CaseDetail caseId={openCaseId} onClose={() => setOpenCaseId(null)} onChanged={load} />}
    </>
  );
}
